import { z } from 'zod';
import { normalizedUuidSchema as uuidSchema } from './common';
import { guestContactSchema, guestKeySchema } from './guest-contact';
import {
  GUEST_QUOTE_PRIVACY_CONSENT_REQUIRED_MESSAGE,
  guestQuoteRequestFormInputSchema,
  guestQuoteRequestFormSchema,
} from './guest-quotes';
import { refineDestinationSizes } from './quote-sizes';
import {
  quoteCalendarDateSchema,
  quoteOptionalTextSchema,
  quoteRequiredTextSchema,
  refineDestinationTotal,
} from './quotes';
import {
  destinationListSchema,
  guestDestinationInputSchema,
  memberDestinationInputSchema,
} from './shipping';

// ---------------------------------------------------------------------------
// 일반 견적 — 편집영역이 없는(인쇄 없는) 상품을 디자인·인쇄 방식 없이 바로 견적 요청하는 계약.
// 설계: docs/superpowers/specs/2026-10-30-plain-product-quote-design.md (일반 견적 설계 §5-3).
// `quotes.ts`·`guest-quotes.ts` 를 import 하기만 하고, 그쪽은 이 파일을 import 하지 않는다(순환 금지).
// ---------------------------------------------------------------------------

/** 요청 폼 조회 입력 — 회원·비회원 모두 상품·색상으로 묻는다(저장한 디자인이 없다). */
export const plainQuoteRequestFormInputSchema = guestQuoteRequestFormInputSchema;
export type PlainQuoteRequestFormInput = z.infer<typeof plainQuoteRequestFormInputSchema>;

/** 요청 폼 — 비회원 폼 칸에 회원 로그인 시 채울 기본 담당자명이 더해진다(비회원은 빈 문자열). */
export const plainQuoteRequestFormSchema = guestQuoteRequestFormSchema.extend({
  defaultContactName: z.string(),
});
export type PlainQuoteRequestForm = z.infer<typeof plainQuoteRequestFormSchema>;

/** 회원 일반 견적 요청 제출 — `quoteRequestInputSchema` 에서 디자인·인쇄 방식을 빼고 상품·색상을 직접 받는다. */
export const plainQuoteRequestInputSchema = z
  .strictObject({
    productId: uuidSchema,
    colorOptionId: uuidSchema,
    desiredDate: quoteCalendarDateSchema.nullable(),
    destinations: destinationListSchema(memberDestinationInputSchema),
    memo: quoteOptionalTextSchema(1000),
    companyName: quoteOptionalTextSchema(100),
    contactName: quoteRequiredTextSchema.max(50),
  })
  .superRefine((input, ctx) => {
    refineDestinationTotal(input.destinations, ctx);
    refineDestinationSizes(input.destinations, ctx);
  });
export type PlainQuoteRequestInput = z.infer<typeof plainQuoteRequestInputSchema>;

/** 비회원 일반 견적 요청 제출 — `guestQuoteRequestInputSchema` 에서 `design`·`printMethodId` 를 뺀 같은 칸. */
export const guestPlainQuoteRequestInputSchema = z
  .strictObject({
    guestKey: guestKeySchema,
    productId: uuidSchema,
    colorOptionId: uuidSchema,
    destinations: destinationListSchema(guestDestinationInputSchema),
    desiredDate: quoteCalendarDateSchema.nullable(),
    companyName: quoteOptionalTextSchema(100),
    contactName: quoteRequiredTextSchema.max(50),
    memo: quoteOptionalTextSchema(1000),
    contact: guestContactSchema,
    privacyConsent: z.literal(true, { message: GUEST_QUOTE_PRIVACY_CONSENT_REQUIRED_MESSAGE }),
  })
  .superRefine((input, ctx) => {
    refineDestinationTotal(input.destinations, ctx);
    refineDestinationSizes(input.destinations, ctx);
  });
export type GuestPlainQuoteRequestInput = z.infer<typeof guestPlainQuoteRequestInputSchema>;
